// ============================================================================
// FICHIER : src/pages/admin-saas/SauvegardesPage.tsx
// DESCRIPTION : Interface de gestion des sauvegardes Firebase (Firestore + Storage)
// AUTEUR : Claude (Session 60 + 61)
// DATE : 01/02/2026
// MODIF : Séparation claire Firestore (données) / Storage (fichiers)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  limit,
  where,
  Timestamp 
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../../services/firebase';
import { 
  Database, 
  Download, 
  Trash2, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock,
  AlertTriangle,
  ArrowLeft,
  Play,
  HardDrive,
  Calendar,
  FolderArchive,
  FileArchive,
  Info
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface BackupMetadata {
  id: string;
  type: 'manual' | 'scheduled' | 'firestore' | 'storage';
  status: 'in_progress' | 'completed' | 'failed';
  startedAt: Timestamp;
  completedAt?: Timestamp;
  fileName: string;
  filePath: string;
  fileSize?: number;
  collectionsCount?: number;
  documentsCount?: number;
  filesCount?: number;
  error?: string;
  triggeredBy?: string;
  downloadUrl?: string;
}

// ============================================
// HELPERS
// ============================================

const formatDate = (timestamp: Timestamp | undefined): string => {
  if (!timestamp) return '—';
  const date = timestamp.toDate();
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
};

const getNextScheduledBackup = (): string => {
  const now = new Date();
  const next = new Date();
  next.setHours(3, 0, 0, 0);
  
  if (now.getHours() >= 3) {
    next.setDate(next.getDate() + 1);
  }
  
  const diffMs = next.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 0) {
    return `dans ${diffHours}h${diffMinutes > 0 ? ` ${diffMinutes}min` : ''}`;
  }
  return `dans ${diffMinutes} minutes`;
};

// ============================================
// COMPOSANT
// ============================================

const SauvegardesPage: React.FC = () => {
  // États Firestore
  const [loadingFirestore, setLoadingFirestore] = useState(true);
  const [firestoreBackups, setFirestoreBackups] = useState<BackupMetadata[]>([]);
  const [firestoreBackupInProgress, setFirestoreBackupInProgress] = useState(false);
  
  // États Storage
  const [loadingStorage, setLoadingStorage] = useState(true);
  const [storageBackups, setStorageBackups] = useState<BackupMetadata[]>([]);
  const [storageBackupInProgress, setStorageBackupInProgress] = useState(false);
  
  // États communs
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ============================================
  // CHARGEMENT FIRESTORE BACKUPS
  // ============================================
  
  const loadFirestoreBackups = async () => {
    try {
      const q = query(
        collection(db, 'backups'),
        where('type', 'in', ['manual', 'scheduled', 'firestore']),
        orderBy('startedAt', 'desc'),
        limit(20)
      );
      
      const snapshot = await getDocs(q);
      const backupList: BackupMetadata[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as BackupMetadata));
      
      setFirestoreBackups(backupList);
      
      const inProgress = backupList.some(b => b.status === 'in_progress');
      setFirestoreBackupInProgress(inProgress);
      
    } catch (err: any) {
      console.error('Erreur chargement backups Firestore:', err);
    } finally {
      setLoadingFirestore(false);
    }
  };

  // ============================================
  // CHARGEMENT STORAGE BACKUPS
  // ============================================
  
  const loadStorageBackups = async () => {
    try {
      const q = query(
        collection(db, 'backups'),
        where('type', '==', 'storage'),
        orderBy('startedAt', 'desc'),
        limit(20)
      );
      
      const snapshot = await getDocs(q);
      const backupList: BackupMetadata[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as BackupMetadata));
      
      setStorageBackups(backupList);
      
      const inProgress = backupList.some(b => b.status === 'in_progress');
      setStorageBackupInProgress(inProgress);
      
    } catch (err: any) {
      console.error('Erreur chargement backups Storage:', err);
    } finally {
      setLoadingStorage(false);
    }
  };

  useEffect(() => {
    loadFirestoreBackups();
    loadStorageBackups();
    
    // Rafraîchir toutes les 30 secondes si backup en cours
    const interval = setInterval(() => {
      if (firestoreBackupInProgress) loadFirestoreBackups();
      if (storageBackupInProgress) loadStorageBackups();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [firestoreBackupInProgress, storageBackupInProgress]);

  // ============================================
  // ACTIONS FIRESTORE
  // ============================================

  const handleFirestoreBackup = async () => {
    setFirestoreBackupInProgress(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const triggerBackup = httpsCallable(functions, 'triggerManualBackup');
      const result = await triggerBackup();
      const data = result.data as any;
      
      setSuccessMessage(`Sauvegarde Firestore créée : ${data.documentsCount} documents`);
      await loadFirestoreBackups();
      
    } catch (err: any) {
      console.error('Erreur backup Firestore:', err);
      setError(err.message || 'Erreur lors de la sauvegarde Firestore');
    } finally {
      setFirestoreBackupInProgress(false);
    }
  };

  const handleFirestoreDownload = async (backup: BackupMetadata) => {
    setDownloadingId(backup.id);
    setError(null);
    
    try {
      const fileRef = ref(storage, backup.filePath);
      const downloadUrl = await getDownloadURL(fileRef);
      window.open(downloadUrl, '_blank');
    } catch (err: any) {
      console.error('Erreur téléchargement:', err);
      if (err.code === 'storage/unauthorized') {
        setError('Accès non autorisé au fichier');
      } else if (err.code === 'storage/object-not-found') {
        setError('Fichier introuvable');
      } else {
        setError(err.message || 'Erreur lors du téléchargement');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFirestoreDelete = async (backupId: string) => {
    if (!window.confirm('Supprimer définitivement cette sauvegarde Firestore ?')) return;
    
    setDeletingId(backupId);
    setError(null);
    
    try {
      const removeBackupFn = httpsCallable(functions, 'removeBackup');
      await removeBackupFn({ backupId });
      
      setSuccessMessage('Sauvegarde Firestore supprimée');
      await loadFirestoreBackups();
    } catch (err: any) {
      console.error('Erreur suppression:', err);
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // ============================================
  // ACTIONS STORAGE
  // ============================================

  const handleStorageBackup = async () => {
    setStorageBackupInProgress(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const triggerBackup = httpsCallable(functions, 'triggerStorageBackup');
      const result = await triggerBackup();
      const data = result.data as any;
      
      setSuccessMessage(`Sauvegarde Storage créée : ${data.filesCount} fichiers (${formatFileSize(data.fileSize)})`);
      await loadStorageBackups();
      
    } catch (err: any) {
      console.error('Erreur backup Storage:', err);
      setError(err.message || 'Erreur lors de la sauvegarde Storage');
    } finally {
      setStorageBackupInProgress(false);
    }
  };

  const handleStorageDownload = async (backup: BackupMetadata) => {
    setDownloadingId(backup.id);
    setError(null);
    
    try {
      // Si l'URL signée existe et est récente, l'utiliser directement
      if (backup.downloadUrl) {
        window.open(backup.downloadUrl, '_blank');
      } else {
        // Sinon, obtenir une nouvelle URL
        const fileRef = ref(storage, backup.filePath);
        const downloadUrl = await getDownloadURL(fileRef);
        window.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Erreur téléchargement:', err);
      setError(err.message || 'Erreur lors du téléchargement');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleStorageDelete = async (backupId: string) => {
    if (!window.confirm('Supprimer définitivement cette sauvegarde Storage ?')) return;
    
    setDeletingId(backupId);
    setError(null);
    
    try {
      const removeBackupFn = httpsCallable(functions, 'removeStorageBackup');
      await removeBackupFn({ backupId });
      
      setSuccessMessage('Sauvegarde Storage supprimée');
      await loadStorageBackups();
    } catch (err: any) {
      console.error('Erreur suppression:', err);
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // ============================================
  // REFRESH
  // ============================================

  const handleRefresh = async () => {
    setLoadingFirestore(true);
    setLoadingStorage(true);
    await Promise.all([loadFirestoreBackups(), loadStorageBackups()]);
  };

  // ============================================
  // COMPOSANTS UI
  // ============================================

  const StatusBadge: React.FC<{ status: BackupMetadata['status'] }> = ({ status }) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <CheckCircle2 size={14} />
            Terminé
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <RefreshCw size={14} className="animate-spin" />
            En cours
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={14} />
            Échec
          </span>
        );
      default:
        return null;
    }
  };

  // Dernières sauvegardes réussies
  const lastFirestoreBackup = firestoreBackups.find(b => b.status === 'completed');
  const lastStorageBackup = storageBackups.find(b => b.status === 'completed');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin-saas"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sauvegardes</h1>
            <p className="text-gray-500">Gestion des backups Firebase</p>
          </div>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={loadingFirestore || loadingStorage}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Rafraîchir"
        >
          <RefreshCw size={20} className={(loadingFirestore || loadingStorage) ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <XCircle size={18} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="text-emerald-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="font-medium text-emerald-800">Succès</p>
            <p className="text-sm text-emerald-600">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-emerald-400 hover:text-emerald-600">
            <XCircle size={18} />
          </button>
        </div>
      )}

      {/* ================================================================ */}
      {/* SECTION 1 : SAUVEGARDES FIRESTORE (Données)                      */}
      {/* ================================================================ */}
      
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header section Firestore */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Database className="text-blue-600" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sauvegardes Firestore</h2>
                <p className="text-sm text-gray-500">Données : collections, documents, utilisateurs</p>
              </div>
            </div>
            
            <button
              onClick={handleFirestoreBackup}
              disabled={firestoreBackupInProgress}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {firestoreBackupInProgress ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Sauvegarde en cours...
                </>
              ) : (
                <>
                  <Play size={18} />
                  Sauvegarder Firestore
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats Firestore */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="text-gray-400" size={18} />
              <div>
                <p className="text-xs text-gray-500">Prochaine auto</p>
                <p className="text-sm font-medium text-gray-900">{getNextScheduledBackup()}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className={lastFirestoreBackup ? 'text-emerald-500' : 'text-gray-300'} size={18} />
              <div>
                <p className="text-xs text-gray-500">Dernière sauvegarde</p>
                <p className="text-sm font-medium text-gray-900">
                  {lastFirestoreBackup ? formatDate(lastFirestoreBackup.completedAt) : 'Aucune'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <HardDrive className="text-gray-400" size={18} />
              <div>
                <p className="text-xs text-gray-500">Sauvegardes conservées</p>
                <p className="text-sm font-medium text-gray-900">
                  {firestoreBackups.filter(b => b.status === 'completed').length} / 30 jours
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste Firestore */}
        <div className="overflow-x-auto">
          {loadingFirestore ? (
            <div className="p-8 text-center">
              <RefreshCw className="mx-auto text-gray-300 animate-spin mb-3" size={32} />
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : firestoreBackups.length === 0 ? (
            <div className="p-8 text-center">
              <Database className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500">Aucune sauvegarde Firestore</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Statut</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Documents</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Taille</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {firestoreBackups.slice(0, 10).map((backup) => (
                  <tr key={backup.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900 text-sm">{formatDate(backup.startedAt)}</p>
                      <p className="text-xs text-gray-500">
                        {backup.type === 'scheduled' ? 'Auto' : 'Manuel'}
                        {backup.triggeredBy && ` • ${backup.triggeredBy}`}
                      </p>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusBadge status={backup.status} />
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 text-sm">
                      {backup.documentsCount || '—'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 text-sm">
                      {formatFileSize(backup.fileSize)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {backup.status === 'completed' && (
                          <button
                            onClick={() => handleFirestoreDownload(backup)}
                            disabled={downloadingId === backup.id}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="Télécharger"
                          >
                            {downloadingId === backup.id ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <Download size={16} />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleFirestoreDelete(backup.id)}
                          disabled={deletingId === backup.id || backup.status === 'in_progress'}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Supprimer"
                        >
                          {deletingId === backup.id ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 2 : SAUVEGARDES STORAGE (Fichiers)                       */}
      {/* ================================================================ */}
      
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header section Storage */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FolderArchive className="text-amber-600" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sauvegardes Storage</h2>
                <p className="text-sm text-gray-500">Fichiers : photos, documents PDF, sceaux, pièces jointes</p>
              </div>
            </div>
            
            <button
              onClick={handleStorageBackup}
              disabled={storageBackupInProgress}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {storageBackupInProgress ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Création du ZIP...
                </>
              ) : (
                <>
                  <FileArchive size={18} />
                  Sauvegarder Storage
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Storage */}
        <div className="px-6 py-3 border-b border-gray-100 bg-amber-50/50">
          <div className="flex items-start gap-2">
            <Info className="text-amber-500 flex-shrink-0 mt-0.5" size={16} />
            <p className="text-sm text-amber-700">
              Cette sauvegarde crée un fichier ZIP contenant tous les fichiers uploadés. 
              La durée dépend du volume de données (peut prendre plusieurs minutes).
            </p>
          </div>
        </div>

        {/* Stats Storage */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className={lastStorageBackup ? 'text-emerald-500' : 'text-gray-300'} size={18} />
              <div>
                <p className="text-xs text-gray-500">Dernière sauvegarde</p>
                <p className="text-sm font-medium text-gray-900">
                  {lastStorageBackup 
                    ? `${formatDate(lastStorageBackup.completedAt)} • ${lastStorageBackup.filesCount || 0} fichiers`
                    : 'Aucune'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <HardDrive className="text-gray-400" size={18} />
              <div>
                <p className="text-xs text-gray-500">Sauvegardes conservées</p>
                <p className="text-sm font-medium text-gray-900">
                  {storageBackups.filter(b => b.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste Storage */}
        <div className="overflow-x-auto">
          {loadingStorage ? (
            <div className="p-8 text-center">
              <RefreshCw className="mx-auto text-gray-300 animate-spin mb-3" size={32} />
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : storageBackups.length === 0 ? (
            <div className="p-8 text-center">
              <FolderArchive className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500 mb-4">Aucune sauvegarde Storage</p>
              <button
                onClick={handleStorageBackup}
                disabled={storageBackupInProgress}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <FileArchive size={18} />
                Créer la première sauvegarde
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Statut</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Fichiers</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Taille ZIP</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {storageBackups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900 text-sm">{formatDate(backup.startedAt)}</p>
                      <p className="text-xs text-gray-500">{backup.fileName}</p>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusBadge status={backup.status} />
                      {backup.status === 'failed' && backup.error && (
                        <p className="text-xs text-red-500 mt-1">{backup.error.substring(0, 40)}...</p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 text-sm">
                      {backup.filesCount || '—'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 text-sm">
                      {formatFileSize(backup.fileSize)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {backup.status === 'completed' && (
                          <button
                            onClick={() => handleStorageDownload(backup)}
                            disabled={downloadingId === backup.id}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
                            title="Télécharger ZIP"
                          >
                            {downloadingId === backup.id ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <Download size={16} />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleStorageDelete(backup.id)}
                          disabled={deletingId === backup.id || backup.status === 'in_progress'}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Supprimer"
                        >
                          {deletingId === backup.id ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Info rétention */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-blue-800">Recommandations</p>
          <ul className="text-sm text-blue-600 mt-1 space-y-1">
            <li>• <strong>Firestore</strong> : Sauvegarde automatique chaque nuit à 3h00 (rétention 30 jours)</li>
            <li>• <strong>Storage</strong> : Sauvegarde manuelle recommandée chaque semaine ou après ajout de documents importants</li>
            <li>• Téléchargez régulièrement les sauvegardes sur un stockage externe (Google Drive, disque dur)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SauvegardesPage;
